import { AlgoType } from '@/types/generated/algo'
import { OrderType, TimeInForce } from '@/types/generated/common/trading'
import { TradeVenue } from '@/types/generated/common/types'
import type { VenueInfo } from '@/types/generated/public_api'

/**
 * What a trade venue can place, as the order form needs it. The server's
 * `VenueInfo.capabilities` is the source of truth; `FALLBACK_CAPS` only covers
 * the time before GetVenues answers and a backend that predates the field, and
 * mirrors the backend's prod venue config (oems `shared/config/configs/prod.toml`).
 */
export type VenueCaps = {
  orderTypes: OrderType[]
  timeInForce: TimeInForce[]
  postOnly: boolean
  reduceOnly: boolean
  amend: boolean
  venueTriggers: boolean
  partialClose: boolean
  rfq: boolean
  supportedAlgos: AlgoType[]
}

export type ResolvedCaps = {
  caps: VenueCaps
  /** False for the fallback table: callers must not reset persisted choices on it. */
  fromServer: boolean
}

const { ORDER_TYPE_MARKET: MARKET, ORDER_TYPE_LIMIT: LIMIT } = OrderType
const { TIF_GTC: GTC, TIF_IOC: IOC, TIF_FOK: FOK, TIF_GTD: GTD } = TimeInForce

const DENY_ALL: VenueCaps = {
  orderTypes: [MARKET],
  timeInForce: [],
  postOnly: false,
  reduceOnly: false,
  amend: false,
  venueTriggers: false,
  partialClose: false,
  rfq: false,
  supportedAlgos: [],
}

/**
 * The caps of a card with no venue to restrict it (empty ticket, no account): everything is
 * offered, and it is never `fromServer`, so no snap or reset fires on it.
 */
export const IDENTITY_CAPS: VenueCaps = {
  orderTypes: [MARKET, LIMIT],
  timeInForce: [GTC, IOC, FOK, GTD],
  postOnly: true,
  reduceOnly: true,
  amend: true,
  venueTriggers: true,
  partialClose: true,
  rfq: true,
  supportedAlgos: Object.values(AlgoType).filter(
    (a): a is AlgoType => typeof a === 'number' && a > 0
  ),
}

// `supportedAlgos` is empty everywhere: it is derived server-side, so algo tabs
// appear once the server answers.
const caps = (over: Partial<VenueCaps>): VenueCaps => ({
  orderTypes: [MARKET, LIMIT],
  timeInForce: [GTC, IOC, FOK],
  postOnly: true,
  reduceOnly: true,
  amend: true,
  venueTriggers: true,
  partialClose: true,
  rfq: false,
  supportedAlgos: [],
  ...over,
})

const FALLBACK_CAPS: Partial<Record<TradeVenue, VenueCaps>> = {
  [TradeVenue.TRADE_VENUE_DERIBIT]: caps({ timeInForce: [GTC, IOC, FOK, GTD], rfq: true }),
  [TradeVenue.TRADE_VENUE_BYBIT]: caps({}),
  [TradeVenue.TRADE_VENUE_HYPERLIQUID]: caps({ timeInForce: [GTC, IOC] }),
  [TradeVenue.TRADE_VENUE_UPBIT]: caps({ reduceOnly: false, amend: false, venueTriggers: false }),
  [TradeVenue.TRADE_VENUE_DERIVE]: caps({ venueTriggers: false, rfq: true }),
  [TradeVenue.TRADE_VENUE_LIGHTER]: caps({ timeInForce: [GTC, IOC] }),
  [TradeVenue.TRADE_VENUE_TOSS]: caps({
    timeInForce: [GTC],
    postOnly: false,
    reduceOnly: false,
    venueTriggers: false,
  }),
  [TradeVenue.TRADE_VENUE_PAPERTRADE]: caps({
    orderTypes: [MARKET],
    timeInForce: [IOC],
    postOnly: false,
    amend: false,
    venueTriggers: false,
  }),
}

export function resolveVenueCaps(
  venues: VenueInfo[] | undefined,
  tv: TradeVenue | null | undefined
): ResolvedCaps {
  if (tv === null || tv === undefined) return { caps: DENY_ALL, fromServer: false }
  const served = venues?.find((v) => v.tradeVenue === tv)?.capabilities
  if (served) return { caps: served, fromServer: true }
  return { caps: FALLBACK_CAPS[tv] ?? DENY_ALL, fromServer: false }
}

const intersect = <T>(lists: T[][]): T[] =>
  lists.reduce((acc, list) => acc.filter((x) => list.includes(x)))

/**
 * Card level: an option is offered only if every row's venue supports it. No rows is the
 * identity (nothing restricts the card yet), never fromServer.
 */
export function intersectCaps(all: ResolvedCaps[]): ResolvedCaps {
  if (all.length === 0) return { caps: IDENTITY_CAPS, fromServer: false }
  const every = (pick: (c: VenueCaps) => boolean) => all.every((r) => pick(r.caps))
  return {
    caps: {
      orderTypes: intersect(all.map((r) => r.caps.orderTypes)),
      timeInForce: intersect(all.map((r) => r.caps.timeInForce)),
      postOnly: every((c) => c.postOnly),
      reduceOnly: every((c) => c.reduceOnly),
      amend: every((c) => c.amend),
      venueTriggers: every((c) => c.venueTriggers),
      partialClose: every((c) => c.partialClose),
      rfq: every((c) => c.rfq),
      supportedAlgos: intersect(all.map((r) => r.caps.supportedAlgos)),
    },
    fromServer: all.every((r) => r.fromServer),
  }
}

export function offersTif(c: VenueCaps, tif: TimeInForce): boolean {
  return c.timeInForce.includes(tif)
}

type OrderShape = { isMarketOrder: boolean; timeInForce: TimeInForce }

/** Post-only rests on the book, so it only exists on limit orders with GTC or GTD. */
export function offersPostOnly(c: VenueCaps, o: OrderShape): boolean {
  return c.postOnly && !o.isMarketOrder && (o.timeInForce === GTC || o.timeInForce === GTD)
}

/**
 * Derive rejects reduce-only on a resting order, so any card containing Derive
 * is only offered for market/IOC/FOK.
 */
export function offersReduceOnly(
  c: VenueCaps,
  venues: TradeVenue | readonly TradeVenue[] | null | undefined,
  o: OrderShape
): boolean {
  if (!c.reduceOnly) return false
  const allVenues = typeof venues === 'number' ? [venues] : (venues ?? [])
  if (!allVenues.includes(TradeVenue.TRADE_VENUE_DERIVE)) return true
  return o.isMarketOrder || o.timeInForce === IOC || o.timeInForce === FOK
}

const TIF_PREFERENCE = [GTC, IOC, FOK, GTD]

/**
 * The execution flags the form may hold, given the persisted choices and the caps. A flag
 * the caps don't offer is never kept, so a hidden chip can't be submitted: the persisted
 * config is not touched, and the choice returns when the chip does.
 */
export function reconcileOrderFlags(
  c: VenueCaps,
  venues: TradeVenue | readonly TradeVenue[] | null | undefined,
  o: {
    isMarketOrder: boolean
    isScaleMode: boolean
    timeInForce: TimeInForce
    reduceOnly: boolean
    postOnly: boolean
  }
): { timeInForce: TimeInForce; reduceOnly: boolean; postOnly: boolean } {
  const timeInForce =
    c.timeInForce.length === 0 || offersTif(c, o.timeInForce)
      ? o.timeInForce
      : (TIF_PREFERENCE.find((t) => offersTif(c, t)) ?? c.timeInForce[0])
  // Scale children always rest GTC, whatever TIF the hidden picker holds.
  const shape = { isMarketOrder: o.isMarketOrder, timeInForce: o.isScaleMode ? GTC : timeInForce }
  return {
    timeInForce,
    reduceOnly: o.reduceOnly && offersReduceOnly(c, venues, shape),
    postOnly: o.postOnly && offersPostOnly(c, shape),
  }
}

export function offersAlgo(c: VenueCaps, a: AlgoType): boolean {
  return c.supportedAlgos.includes(a)
}
